/**
 * Defensive HTML sanitizer for admin-authored content rendered via
 * `dangerouslySetInnerHTML`. Strips <script>, event handlers (onerror=,
 * onload=, ...), javascript: URLs, and other XSS vectors while preserving
 * the formatting tags TipTap produces (paragraphs, bold/italic, lists,
 * links, headings, blockquotes).
 *
 * Even though admins are trusted authors, this is defense-in-depth in case
 * a stored DB value is ever tampered with or a non-admin path writes HTML.
 */
import DOMPurify from 'dompurify';

// TipTap-friendly allowlist. Add tags here only if a legitimate editor
// extension produces them (e.g. <s>, <code>, <pre>, <img> if image
// uploads are added).
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'mark',
  'a',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr',
  'code', 'pre',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style', 'title'];

// Defense-in-depth: enforce target=_blank rel on outbound links produced
// after sanitize so the editor's links can't navigate the parent window.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('rel', 'noopener noreferrer');
    if (!node.getAttribute('target')) {
      node.setAttribute('target', '_blank');
    }
  }
});

export function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Strip blocks that contain executable content even via tricky encodings
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });
}

/**
 * Escape a string so it is safe to interpolate into raw HTML produced by
 * document.write or template strings (e.g. the print-roster window).
 * Use this whenever you cannot use React or sanitizeHtml.
 */
export function escapeHtml(s) {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Plain-text extraction from admin-authored HTML, for contexts that must not
 * contain markup at all — e.g. schema.org JSON-LD `description` fields, which
 * are plain strings, not HTML.
 */
export function stripHtmlToText(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Safe JSON for JSON-LD <script> blocks. JSON.stringify alone does NOT
 * escape `</script>` sequences, so an attacker-controlled value in the
 * payload could break out of the script tag. This escapes `<`, `>`, and
 * line/paragraph separators which can also terminate JS strings.
 */
export function safeJsonLd(obj) {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
